
import React, { useEffect } from 'react';
import BankConnectionDialog from '@/components/cash/BankConnectionDialog';
import BankAccountsCard from '@/components/cash/BankAccountsCard';
import { BankPageProvider } from '@/components/providers/BankPageProvider';
import { Loader2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useTranslations } from 'use-intl';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import { BankAccountSummary } from '@/types/accounts';


const CashManagement = () => {
  const location = useLocation();
  const t = useTranslations('consultantTransactions');
  const {
    accounts,
    setAccounts,
    loadingAccounts: loading,
    accountsError: error,
    consentRequiredError,
    loadAccounts,
    handleConnectBank
  } = useBankAccounts();

  // Add debug log to track component mounting  
  console.log("🏠 CashManagement RENDER at path:", location.pathname);
  console.log("🏠 CashManagement state:", {
    loading,
    error,
    accountsCount: accounts.length,
    consentRequiredError,
    shouldShowDialog: !!consentRequiredError
  });
  
  // Force log the consentRequiredError every render
  if (consentRequiredError) {
    console.log("🏠 CashManagement: consentRequiredError EXISTS:", consentRequiredError);
  } else {
    console.log("🏠 CashManagement: consentRequiredError is NULL/UNDEFINED");
  }

  // Debug: Track consentRequiredError changes in CashManagement
  useEffect(() => {
    console.log("📱 CashManagement: ConsentRequiredError changed:", consentRequiredError);
    if (consentRequiredError) {
      console.log("📱 CashManagement: Should show ConsentRefreshDialog now!");
    }
  }, [consentRequiredError]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-xl">{t('accounts.loading')}</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center p-6 bg-red-50 rounded-lg border border-red-200">
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={loadAccounts}
          className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
        >
          {t('page.tryAgain')}
        </button>
      </div>
    );
  }

  const handleAccountsRefresh = (updatedAccounts: BankAccountSummary[]) => {
    console.log("CashManagement: Received updated accounts from consent refresh:", updatedAccounts);
    setAccounts(updatedAccounts);
  };

  const content = (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('accounts.title')}</h1>
        </div>
        
        <BankConnectionDialog
          onConnect={handleConnectBank}
        />
      </div>

      <BankAccountsCard accounts={accounts} setAccounts={setAccounts} onMappingChanged={loadAccounts} />

    </div>
  );

  return (
    <BankPageProvider onAccountsRefresh={handleAccountsRefresh}>
      {content}
    </BankPageProvider>
  );
};

export default CashManagement;
